import React from 'react';
import { logAuditEvent, EVENT_TYPES, LOG_LEVELS } from '../utils/auditLogger';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the app.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    const errorDetails = {
      message: error?.message || 'Unknown error',
      stack: error?.stack || 'No stack trace',
      componentStack: errorInfo?.componentStack || 'No component stack',
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by Error Boundary:', errorDetails);
    }

    // Log to audit system (async, don't block)
    // Use SYSTEM_ERROR event type with ERROR level for unhandled errors
    logAuditEvent(
      EVENT_TYPES.SYSTEM_ERROR,
      LOG_LEVELS.ERROR,
      'Application error caught by Error Boundary',
      errorDetails
    ).catch(err => {
      // Fallback if audit logging fails
      console.error('Failed to log error to audit system:', err);
    });

    // Update state with error info
    this.setState({
      errorInfo,
    });
  }

  handleReload = () => {
    // Clear error state and reload page
    window.location.reload();
  };

  handleReset = () => {
    // Reset error state (may not work if error is persistent)
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo, this.handleReload);
      }

      // Default fallback UI
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
            backgroundColor: '#1a1a1a',
            color: '#e0e0e0',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          <div
            style={{
              maxWidth: '600px',
              width: '100%',
              textAlign: 'center',
            }}
          >
            <h1
              style={{
                fontSize: '48px',
                marginBottom: '16px',
                color: '#B085E2',
              }}
            >
              ⚠️
            </h1>
            <h2
              style={{
                fontSize: '24px',
                marginBottom: '12px',
                color: '#e0e0e0',
              }}
            >
              Something went wrong
            </h2>
            <p
              style={{
                fontSize: '16px',
                marginBottom: '24px',
                color: '#aaa',
                lineHeight: '1.5',
              }}
            >
              We're sorry, but something unexpected happened. Our team has been notified.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details
                style={{
                  marginBottom: '24px',
                  padding: '16px',
                  backgroundColor: '#2a2a2a',
                  borderRadius: '8px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                <summary style={{ cursor: 'pointer', marginBottom: '8px', color: '#B085E2' }}>
                  Error Details (Development Only)
                </summary>
                <div style={{ color: '#ff6b6b' }}>
                  <strong>Error:</strong> {this.state.error.toString()}
                </div>
                {this.state.errorInfo && (
                  <div style={{ marginTop: '12px', color: '#aaa' }}>
                    <strong>Component Stack:</strong>
                    <pre
                      style={{
                        marginTop: '8px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                )}
                {this.state.errorId && (
                  <div style={{ marginTop: '12px', color: '#888', fontSize: '10px' }}>
                    Error ID: {this.state.errorId}
                  </div>
                )}
              </details>
            )}

            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={this.handleReload}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#B085E2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#9a6fc2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#B085E2';
                }}
              >
                Reload Page
              </button>
              <button
                onClick={this.handleReset}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#555';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#444';
                }}
              >
                Try Again
              </button>
            </div>

            {this.state.errorId && (
              <p
                style={{
                  marginTop: '24px',
                  fontSize: '12px',
                  color: '#666',
                }}
              >
                Error ID: {this.state.errorId}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

